import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Crown, Star, CheckCircle, Sparkles, Check, X, Phone, Mail, Clock } from 'lucide-react'
import { usePublicSubscriptionTiers, config, useClientConfig } from '@workspace/public-utils'
import { Seo } from '@workspace/public-ui/components/Seo'

interface CompanyInfo {
  name: string | null
  contact_email: string | null
  contact_number: string | null
  contact_number_secondary: string | null
  working_hours: string | null
  address: string | null
  bank_info: any | null
}

const tierConfig: Record<string, {
  icon: any
  gradient: string
  textColor: string
  iconColor: string
  accentColor: string
}> = {
  SELECT: {
    icon: CheckCircle,
    gradient: 'from-[#F5EBE0] to-[#E5D3C8]',
    textColor: 'text-gray-900',
    iconColor: 'text-amber-600',
    accentColor: 'bg-amber-600',
  },
  STANDARD: {
    icon: CheckCircle,
    gradient: 'from-[#C84B31] to-[#A73A24]',
    textColor: 'text-white',
    iconColor: 'text-amber-400',
    accentColor: 'bg-amber-400',
  },
  PRIME: {
    icon: Sparkles,
    gradient: 'from-[#D4AF37] to-[#B8941F]',
    textColor: 'text-gray-900',
    iconColor: 'text-amber-700',
    accentColor: 'bg-amber-700',
  },
  PRESTIGE: {
    icon: Star,
    gradient: 'from-[#E8B4BC] to-[#D9A5AD]',
    textColor: 'text-gray-900',
    iconColor: 'text-pink-600',
    accentColor: 'bg-pink-600',
  },
  ROYAL: {
    icon: Crown,
    gradient: 'from-[#1E3A5F] to-[#0F1E2E]',
    textColor: 'text-white',
    iconColor: 'text-amber-400',
    accentColor: 'bg-amber-400',
  },
}

interface PlanCardProps {
  tier: any
  index: number
  isPopular?: boolean
  onConsultClick: () => void
}

const HorizontalPlanCard: React.FC<PlanCardProps> = ({ tier, index, isPopular, onConsultClick }) => {
  const cfg = tierConfig[tier.name as keyof typeof tierConfig] || tierConfig.SELECT
  const Icon = cfg.icon

  const isSoldOut = !!tier.sold_out_mark
  const isDisabled = tier.is_disabled || false
  const soldOutColor = tier.sold_out_color || 'RED'

  const badgeColors: Record<string, string> = {
    RED: 'bg-red-600 text-white',
    ORANGE: 'bg-orange-600 text-white',
    YELLOW: 'bg-yellow-500 text-gray-900',
    BLUE: 'bg-blue-600 text-white',
    SILVER: '',
  }

  return (
    <motion.div
      className="relative w-full"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={!isDisabled ? { y: -4 } : {}}
    >
      {isSoldOut && (
        <div className="absolute -top-3 -right-3 z-20">
          <div className={`${badgeColors[soldOutColor]} ${soldOutColor === 'SILVER' ? 'silver-soldout-badge' : ''} px-6 py-2 rounded-full text-2xl font-bold shadow-lg transform rotate-12`}>
            {soldOutColor === 'SILVER' ? (
              <span className="shimmer-silver-text">{tier.sold_out_mark}</span>
            ) : (
              tier.sold_out_mark
            )}
          </div>
        </div>
      )}

      {isPopular && !isSoldOut && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <span className="bg-gradient-to-r from-star-gold to-star-gold-light text-star-navy px-6 py-2 rounded-full text-sm font-bold shadow-lg">
            인기 플랜
          </span>
        </div>
      )}

      <div
        className={`
          bg-gradient-to-r ${cfg.gradient} ${cfg.textColor}
          rounded-2xl shadow-xl overflow-hidden
          ${isDisabled ? 'opacity-50 grayscale cursor-not-allowed' : 'hover:shadow-2xl'}
          transition-all duration-300
        `}
      >
        <div className="flex flex-col md:flex-row items-center p-6 md:p-8 gap-6">
          <div className="flex items-center gap-6 md:w-1/4">
            <div className={`w-20 h-20 rounded-full border-4 ${cfg.textColor === 'text-white' ? 'border-white/30' : 'border-gray-900/20'} flex items-center justify-center flex-shrink-0 overflow-hidden`}>
              {tier.icon_image_url ? (
                <img
                  src={`${config.API_BASE_URL}${tier.icon_image_url}`}
                  alt={tier.name}
                  className="w-16 h-16 object-contain"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden'); }}
                />
              ) : null}
              <Icon className={`w-10 h-10 ${cfg.iconColor}${tier.icon_image_url ? ' hidden' : ''}`} strokeWidth={2.5} />
            </div>
            <div>
              <h3 className="text-3xl font-black tracking-tight !text-white">{tier.name}</h3>
              <p className={`text-base font-bold ${cfg.textColor === 'text-white' ? 'text-white opacity-90' : 'text-white opacity-80'}`}>
                {tier.top_tier_comment ||
                  (tier.name === 'SELECT' ? 'Basic' :
                   tier.name === 'STANDARD' ? 'Selected' :
                   tier.name === 'PRIME' ? 'Premium' :
                   tier.name === 'PRESTIGE' ? 'High-level' :
                   'Top-tier')}
              </p>
            </div>
          </div>

          <div className="flex-1 md:w-2/5">
            <div className={`${cfg.textColor === 'text-white' ? 'bg-white/10' : 'bg-black/5'} backdrop-blur-sm rounded-xl p-4`}>
              <h4 className="text-sm font-bold mb-2 opacity-70">제공 기능</h4>
              {tier.features && tier.features.length > 0 ? (
                <ul className="space-y-1">
                  {tier.features.slice(0, 3).map((feature: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cfg.iconColor}`} />
                      <span className="line-clamp-1">{feature}</span>
                    </li>
                  ))}
                  {tier.features.length > 3 && (
                    <li className="text-xs opacity-70">외 {tier.features.length - 3}개 기능</li>
                  )}
                </ul>
              ) : (
                <p className="text-sm opacity-70">기본 기능 제공</p>
              )}
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 md:w-1/3">
            <div className="text-center">
              <div className={`text-lg ${cfg.textColor === 'text-white' ? 'opacity-80' : 'opacity-70'}`}>
                {tier.max_numbers_per_month}회 생성 가능
              </div>
            </div>

            {!isDisabled ? (
              <button
                onClick={onConsultClick}
                className={`
                  block w-full py-3 px-6 rounded-xl font-bold text-center transition-all duration-300 transform hover:scale-105
                  ${isPopular
                    ? 'bg-white text-star-navy shadow-lg hover:shadow-xl'
                    : cfg.textColor === 'text-white'
                    ? 'bg-white/20 hover:bg-white/30 backdrop-blur-sm'
                    : 'bg-gray-900/10 hover:bg-gray-900/20'
                  }
                `}
              >
                상담신청
              </button>
            ) : (
              <button
                disabled
                className={`
                  block w-full py-3 px-6 rounded-xl font-bold text-center cursor-not-allowed
                  ${cfg.textColor === 'text-white' ? 'bg-white/10' : 'bg-gray-900/10'}
                `}
              >
                {isSoldOut ? tier.sold_out_mark : '이용 불가'}
              </button>
            )}
          </div>
        </div>

        {tier.description && (
          <div className={`px-6 pb-6 ${cfg.textColor === 'text-white' ? 'opacity-80' : 'opacity-70'}`}>
            <p className="text-sm text-center">{tier.description}</p>
          </div>
        )}
      </div>
    </motion.div>
  )
}

const ConsultationModal: React.FC<{ isOpen: boolean; onClose: () => void; companyInfo: CompanyInfo | null }> = ({ isOpen, onClose, companyInfo }) => {
  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-star-navy rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="w-8 h-8 text-star-gold" />
            </div>
            <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-2">플랜 가입 문의</h2>
            <p className="text-gray-600 dark:text-gray-400">
              아래 연락처로 문의주시면 친절하게 안내해드리겠습니다
            </p>
          </div>

          <div className="space-y-4">
            {companyInfo?.contact_number && (
              <a
                href={`tel:${companyInfo.contact_number.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="w-12 h-12 bg-star-navy/10 dark:bg-star-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-star-navy dark:text-star-gold" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-600 dark:text-gray-400">대표 연락처</div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white">{companyInfo.contact_number}</div>
                </div>
              </a>
            )}

            {companyInfo?.contact_number_secondary && (
              <a
                href={`tel:${companyInfo.contact_number_secondary.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="w-12 h-12 bg-star-navy/10 dark:bg-star-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-star-navy dark:text-star-gold" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-600 dark:text-gray-400">고객센터 연락처</div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white">{companyInfo.contact_number_secondary}</div>
                </div>
              </a>
            )}

            {companyInfo?.contact_email && (
              <a
                href={`mailto:${encodeURIComponent(companyInfo.contact_email.trim())}`}
                className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="w-12 h-12 bg-star-navy/10 dark:bg-star-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-star-navy dark:text-star-gold" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-gray-600 dark:text-gray-400">이메일</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">{companyInfo.contact_email}</div>
                </div>
              </a>
            )}

            {companyInfo?.working_hours && (
              <div className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-blue-600 dark:text-blue-400">상담 가능 시간</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">{companyInfo.working_hours}</div>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={onClose}
            className="w-full mt-6 star-btn-primary !rounded-xl"
          >
            닫기
          </button>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

const StarSubscriptionPlansPage: React.FC = () => {
  const clientConfig = useClientConfig()
  const { data: subscriptionTiers, isLoading, error } = usePublicSubscriptionTiers()
  const [showConsultModal, setShowConsultModal] = useState(false)
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/api/v1/public/company-info`)
        if (response.ok) {
          const data = await response.json()
          setCompanyInfo(data)
        }
      } catch (error) {
        console.error('Failed to fetch company info:', error)
      }
    }
    fetchCompanyInfo()
  }, [])

  const handleConsultClick = () => {
    setShowConsultModal(true)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-star-navy border-t-transparent mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">구독 플랜을 불러오는 중...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center">
            <p className="text-red-600 mb-4">구독 플랜을 불러올 수 없습니다.</p>
            <button onClick={() => window.location.reload()} className="star-btn-primary">
              다시 시도
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Seo
        title="구독 플랜"
        description="구독 등급별 혜택과 가격을 확인하고, 나에게 맞는 로또 번호 서비스 플랜을 선택해 보세요."
        canonicalPath="/plans"
      />
      <ConsultationModal isOpen={showConsultModal} onClose={() => setShowConsultModal(false)} companyInfo={companyInfo} />

      {/* Hero Section — STAR-GEN Branding */}
      <section className="relative bg-white dark:bg-gray-900 py-12 flex items-center justify-center transition-colors">
        <div className="relative w-full max-w-2xl mx-auto p-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-7xl md:text-8xl font-black tracking-wider text-star-navy dark:text-white uppercase mb-2"
          >
            STAR<span className="text-star-gold">-GEN</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-lg md:text-xl text-gray-500 dark:text-gray-400 tracking-widest font-light mb-6"
          >
            AI ALGORITHM SYSTEM
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="my-6 h-px bg-gradient-to-r from-gray-200 dark:from-gray-700 via-star-gold to-gray-200 dark:to-gray-700"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl md:text-2xl font-bold text-star-navy dark:text-white leading-relaxed mb-6"
          >
            "당첨되지 않은 이유를 분석할 때,<br />비로소 확률은 움직인다"
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="my-6 h-px bg-gradient-to-r from-gray-200 dark:from-gray-700 via-blue-400 to-gray-200 dark:to-gray-700"
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-gray-500 dark:text-gray-400"
          >
            <p className="text-sm">Powered by</p>
            <p className="text-2xl font-bold mt-1 text-star-navy dark:text-white">
              {clientConfig.brandNameEn} {clientConfig.brandName}
            </p>
            <p className="mt-3 text-xs tracking-widest text-gray-400 dark:text-gray-500">
              STAR-GEN AI DIVISION
            </p>
          </motion.div>
        </div>
      </section>

      {/* Plans Section */}
      <section className="py-20 bg-gradient-to-b from-white dark:from-gray-900 via-gray-50 dark:via-gray-950 to-white dark:to-gray-900 transition-colors">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-black text-star-navy dark:text-white mb-6">
              플랜별 상세 비교
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              당신에게 맞는 완벽한 플랜을 선택하세요
            </p>
          </motion.div>

          {subscriptionTiers && subscriptionTiers.length > 0 ? (
            <div className="space-y-6">
              {subscriptionTiers
                .sort((a: any, b: any) => a.price - b.price)
                .map((tier: any, index: number) => (
                  <HorizontalPlanCard
                    key={tier.id}
                    tier={tier}
                    index={index}
                    isPopular={false}
                    onConsultClick={handleConsultClick}
                  />
                ))}
            </div>
          ) : (
            <div className="text-center text-gray-600 dark:text-gray-400 py-12">
              <p className="text-xl">구독 플랜을 불러올 수 없습니다.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default StarSubscriptionPlansPage
